import React from 'react';
import cx from 'classnames';
import { size, get } from 'lodash/fp';
import HeatmapLegend from './HeatmapLegend';
import {
    HeatmapCellType,
    HeatmapCellPosition,
    HeatmapPercentile,
    HeatmapScoreType,
} from './HeatmapTypes';
import HeatmapCells from './HeatmapCells';
import HeatmapHeader from './Header/HeatmapHeader';
import {
    IActiveDimension,
    IActiveClientFacingIndicator,
    E_RISK_TYPE,
} from 'services/APIService';

type Props = {
    cells: HeatmapCellType[][];
    percentiles: HeatmapPercentile[];
    onCellPress: (rowIndex, colIndex) => void;
    onCellMouseEnter: (rowIndex, colIndex) => void;
    onCellMouseLeave: () => void;
    dimensionsList: IActiveDimension[];
    countryNames: string[];
    selectedPercentile: HeatmapPercentile;
    onSelectPercentile: (percentile: HeatmapPercentile) => void;
    hoveredCell: HeatmapCellPosition;
    selectedRiskId: number;
    selectedCountry: string;
    hoveredRiskId: number;
    riskType: E_RISK_TYPE;
    scoreType: HeatmapScoreType;
    onDimensionClick: (dimensionId: number) => void;
    onCountryClick: (countryName: string) => void;
    clientFacingIndicators: IActiveClientFacingIndicator[];
    numDays: number;
};

const HeatmapGrid = React.memo(
    ({
        cells,
        percentiles,
        onCellPress,
        onCellMouseEnter,
        onCellMouseLeave,
        countryNames,
        dimensionsList,
        selectedPercentile,
        onSelectPercentile,
        hoveredCell,
        selectedRiskId,
        hoveredRiskId,
        riskType,
        scoreType,
        onDimensionClick,
        onCountryClick,
        selectedCountry,
        clientFacingIndicators,
        numDays,
    }: Props) => {
        const numRows = size(countryNames);

        return (
            <>
            
                <div className="heatmap-container" data-testid="heatmap">
                    <HeatmapHeader
                    dimensionsList={dimensionsList}
                    hoveredIndicatorId={hoveredRiskId}
                    selectedIndicatorId={selectedRiskId}
                    riskType={riskType}
                    onIndicatorClick={onDimensionClick}
                    clientFacingIndicators={clientFacingIndicators}
                />
                    <div className="heatmap-grid-and-legend-container">
                        <div className="heatmap-grid-and-legend-absolute-container">
                            <div className="heatmap-grid-container">
                                <div
                                    className="heatmap-grid"
                                    onMouseLeave={onCellMouseLeave}>
                                    
                                    <div style={{ gridRow: 1, gridColumn: 1 }} />
                                    <div
                                        style={{
                                            gridRowStart: numRows + 2,
                                            height: '40px',
                                        }}
                                    />
                                    {countryNames.map(
                                        (country: string, rowIndex: number) => (
                                            <HeatmapRowHeader
                                                key={country}
                                                country={country}
                                                rowIndex={rowIndex}
                                                selectedCountry={selectedCountry}
                                                hoveredCell={hoveredCell}
                                                onCountryClick={onCountryClick}
                                                onCellMouseEnter={onCellMouseEnter}
                                            />
                                        )
                                    )}
                                    <HeatmapCells
                                        cells={cells}
                                        onCellMouseEnter={onCellMouseEnter}
                                        onCellPress={onCellPress}
                                        riskType={riskType}
                                    />
                                </div>
                            </div>
                            <div
                                className="heatmap-legend-container"
                                style={
                                    numRows === 0
                                        ? { marginLeft: 'auto' }
                                        : undefined
                                }>
                                <HeatmapLegend
                                    percentiles={percentiles}
                                    onSelect={onSelectPercentile}
                                    selectedPercentile={selectedPercentile}
                                    numDays={numDays}
                                    scoreType={scoreType}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
);

const HeatmapRowHeader = ({
    country,
    rowIndex,
    selectedCountry,
    hoveredCell,
    onCountryClick,
    onCellMouseEnter,
}) => (
    <div
        className={cx([
            'heatmap-row-country',
            {
                'heatmap-row-country-selected': selectedCountry === country,
            },
            {
                'heatmap-row-country-hovered':
                    get('row', hoveredCell) === rowIndex,
            },
        ])}
        key={country}
        onClick={() => onCountryClick(country)}
        onMouseEnter={() => onCellMouseEnter(rowIndex, -1)}>
        <div className="heatmap-row-country-text">{country}</div>
    </div>
);

export default HeatmapGrid;
